import React, { useState } from "react";
import { ArrowRight, Clock, Sparkles, Heart } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { BUSINESS, IMG } from "../lib/constants";
import TrustStrip from "../components/TrustStrip";
import FaqSection from "../components/FaqSection";
import BookingCTA from "../components/BookingCTA";
import SEOHead from "../components/SEOHead";
import Lightbox from "../components/Lightbox";

const LashesPage = () => {
    const { t } = useLanguage();
    const s = t.services.lashes;
    const [lightboxIndex, setLightboxIndex] = useState(null);
    
    const gallery = [IMG.nanoComboLashLift, IMG.nanoComboLashLift2, IMG.lashLift];

    const openLightbox = (index) => setLightboxIndex(index);
    const closeLightbox = () => setLightboxIndex(null);
    const nextImage = () => setLightboxIndex((prev) => (prev + 1) % gallery.length);
    const prevImage = () => setLightboxIndex((prev) => (prev - 1 + gallery.length) % gallery.length);

    return (
        <>
            <SEOHead page="lashes" />
            
            {/* Hero */}
            <section
                data-testid="lashes-hero"
                className="relative min-h-[68vh] flex items-end overflow-hidden"
            >
                <div className="absolute inset-0">
                    <img 
                        src={IMG.nanoComboLashLift} 
                        alt="Lash Lift and Lash Extensions Las Vegas - Beauty Ink LV" 
                        className="w-full h-full object-cover" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3D3D24] via-[#3D3D24]/55 to-[#3D3D24]/15" />
                </div>
                <div className="relative w-full max-w-[1400px] mx-auto px-6 pb-16 md:pb-20 z-10">
                    <div className="max-w-3xl">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{s.overline}</div>
                        <h1 className="font-serif text-[#FFFFF3] text-5xl md:text-7xl leading-[0.98] mb-6">
                            {s.title}
                        </h1>
                        <p className="text-[#FFFFF3]/90 text-base md:text-lg max-w-2xl leading-relaxed">
                            {s.subtitle}
                        </p>
                    </div>
                </div>
            </section>

            <TrustStrip variant="dark" />

            {/* Video + Description */}
            <section className="bg-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    {/* Video */}
                    <div className="overflow-hidden aspect-[4/5] bg-[#F5F2E4]">
                        <video
                            src={IMG.videoLashLift}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-full object-cover"
                        />
                    </div>
                    
                    {/* Content */}
                    <div>
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">The Service</div>
                        <p className="font-serif text-2xl md:text-3xl text-[#3D3D24] leading-snug mb-8">
                            {s.description}
                        </p>
                        <div className="border-l-2 border-[#D4A95A] pl-6 mb-10">
                            <div className="font-label text-[10px] text-[#3D3D24]/70 mb-2">Best For</div>
                            <p className="text-[#3D3D24]/85 leading-relaxed">{s.bestFor}</p>
                        </div>
                        
                        {/* At a Glance */}
                        <div className="bg-[#3D3D24] text-[#FFFFF3] p-8">
                            <div className="font-label text-[11px] text-[#D4A95A] mb-6">At a Glance</div>
                            <div className="space-y-5">
                                <div className="flex items-start gap-4 pb-5 border-b border-[#FFFFF3]/15">
                                    <Sparkles size={18} className="text-[#D4A95A] mt-1 shrink-0" />
                                    <div>
                                        <div className="font-label text-[10px] opacity-70">Pricing</div>
                                        <div className="text-base mt-1">{s.pricing}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 pb-5 border-b border-[#FFFFF3]/15">
                                    <Clock size={18} className="text-[#D4A95A] mt-1 shrink-0" />
                                    <div>
                                        <div className="font-label text-[10px] opacity-70">Duration</div>
                                        <div className="text-base mt-1">{s.duration}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <Heart size={18} className="text-[#D4A95A] mt-1 shrink-0" />
                                    <div>
                                        <div className="font-label text-[10px] opacity-70">Lasts</div>
                                        <div className="text-base mt-1">{s.healing}</div>
                                    </div>
                                </div>
                            </div>
                            <a
                                data-testid="lashes-book"
                                href="/contact"
                                className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#D4A95A] text-[#3D3D24] px-6 py-4 font-label text-xs hover:bg-[#FFFFF3] transition-colors"
                            >
                                {t.common.bookConsultation}
                                <ArrowRight size={14} />
                            </a>
                            <div className="text-[10px] font-label text-center mt-4 opacity-65 tracking-[0.2em]">
                                {t.common.depositNote}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Gallery */}
            <section
                data-testid="lashes-gallery"
                className="bg-[#F5F2E4] py-24 md:py-32"
            >
                <div className="max-w-[1400px] mx-auto px-6">
                    <div className="mb-12">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">
                            {t.home.galleryOverline}
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05]">
                            {t.common.gallery} — {s.title}
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 md:gap-6">
                        {gallery.map((src, i) => (
                            <div
                                key={i}
                                data-testid={`gallery-lashes-${i}`}
                                className="overflow-hidden aspect-[4/5] bg-[#F5F2E4] cursor-pointer group"
                                onClick={() => openLightbox(i)}
                            >
                                <img
                                    src={src}
                                    alt={`Lash Lift Las Vegas results ${i + 1} - Beauty Ink LV`}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lightbox */}
            {lightboxIndex !== null && (
                <Lightbox
                    images={gallery}
                    currentIndex={lightboxIndex}
                    onClose={closeLightbox}
                    onNext={nextImage}
                    onPrev={prevImage}
                />
            )}

            <FaqSection />
            <BookingCTA variant="dark" testid="booking-cta-lashes" />
        </>
    );
};

export default LashesPage;
